import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// Initial state
const initialState = {
    maintenancesList: [],
    maintenancesTotalRecords: 0,
    maintenancesLoading: false,
    maintenancesError: null,
    selectedMaintenance: null,
    selectedMaintenanceLoading: false,
    selectedMaintenanceError: null,
    createMaintenanceLoading: false,
    createMaintenanceError: null,
    updateMaintenanceLoading: false,
    updateMaintenanceError: null,
    deleteMaintenanceLoading: false,
    deleteMaintenanceError: null
};

// Async thunks
export const getMaintenancesList = createAsyncThunk(
    "maintenances/getMaintenancesList",
    async ({ accountId, token, search, expirationFilter = 'all', start = 0, length = 10 }, { rejectWithValue }) => {
        try {
            // Endpoint aggiornato per corrispondenza con il backend
            const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL || ''}/maintenances/lists`, {
                headers: {
                    "auth-token": token
                },
                params: {
                    accountId,
                    search: search || '',
                    expirationFilter,
                    start,
                    length,
                    draw: 1
                }
            });
            return {
                data: response.data.data,
                totalRecords: response.data.recordsTotal || 0
            };
        } catch (error) {
            return rejectWithValue(error.response?.data || "Errore nel recupero delle manutenzioni");
        }
    }
);

export const getMaintenanceDetail = createAsyncThunk(
    "maintenances/getMaintenanceDetail",
    async ({ maintenanceId, token }, { rejectWithValue }) => {
        try {
            console.log('[getMaintenanceDetail] Chiamata API per maintenanceId:', maintenanceId);
            // Utilizza lo stesso endpoint usato nel wizard di modifica
            const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL || ''}/maintenances`, {
                headers: {
                    "auth-token": token
                },
                params: {
                    maintenanceId: maintenanceId
                }
            });
            
            console.log('[getMaintenanceDetail] Risposta API:', response.data);
            
            // Gestisce sia il caso in cui i dati siano direttamente in response.data che in response.data.ret
            let maintenanceData;
            if (response.data && response.data._id) {
                // Caso 1: I dati sono direttamente in response.data
                maintenanceData = response.data;
            } else if (response.data && response.data.ret && response.data.ret._id) {
                // Caso 2: I dati sono in response.data.ret
                maintenanceData = response.data.ret;
            } else {
                console.error('[getMaintenanceDetail] Struttura risposta non riconosciuta:', response.data);
                return rejectWithValue("Dati della manutenzione non trovati nella risposta");
            }
            
            console.log('[getMaintenanceDetail] Dati manutenzione estratti:', maintenanceData);
            return maintenanceData;
        } catch (error) {
            console.error('[getMaintenanceDetail] Errore:', error);
            return rejectWithValue(error.response?.data?.message || "Errore nel recupero dei dettagli della manutenzione");
        }
    }
);

export const createMaintenance = createAsyncThunk(
    "maintenances/createMaintenance",
    async ({ maintenance, token }, { rejectWithValue }) => {
        try {
            // Endpoint aggiornato per corrispondenza con il backend
            const response = await axios.post(`${import.meta.env.VITE_API_BASE_URL || ''}/maintenances`, {
                maintenance
            }, {
                headers: {
                    "auth-token": token
                }
            });
            return response.data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Errore nella creazione della manutenzione");
        }
    }
);

export const updateMaintenance = createAsyncThunk(
    "maintenances/updateMaintenance",
    async ({ maintenance, token, force }, { rejectWithValue }) => {
        try {
            // Endpoint aggiornato per corrispondenza con il backend
            const response = await axios.patch(`${import.meta.env.VITE_API_BASE_URL || ''}/maintenances`, {
                maintenance,
                force
            }, {
                headers: {
                    "auth-token": token
                }
            });
            return response.data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Errore nell'aggiornamento della manutenzione");
        }
    }
);

export const deleteMaintenance = createAsyncThunk(
    "maintenances/deleteMaintenance",
    async ({ maintenanceId, token, force }, { rejectWithValue }) => {
        try {
            // Impostazione corretta per la chiamata API di eliminazione
            console.log('Tentativo di eliminazione della manutenzione con ID:', maintenanceId);
            const response = await axios.delete(`${import.meta.env.VITE_API_BASE_URL || ''}/maintenances`, {
                headers: {
                    "auth-token": token
                },
                data: {
                    maintenanceId: maintenanceId,  // Nome parametro corretto (senza la 'e' in più)
                    force
                }
            });

            console.log('Risposta eliminazione:', response.data);
            return maintenanceId;
        } catch (error) {
            console.error('Errore durante l\'eliminazione della manutenzione:', error);
            // Se il backend chiede conferma (commesse collegate) restituisco il payload
            // strutturato cosi' il componente puo' mostrare l'avviso e ripetere con force.
            if (error.response?.data?.requiresConfirmation) {
                return rejectWithValue(error.response.data);
            }
            // Gestione strutturata dell'errore
            const errorMessage = error.response?.data?.message ||
                               error.response?.data?.error ||
                               error.message ||
                               "Errore nell'eliminazione della manutenzione";

            return rejectWithValue(errorMessage);
        }
    }
);

// Slice
const maintenancesSlice = createSlice({
    name: "maintenances",
    initialState,
    reducers: {
        resetSelectedMaintenance: (state) => {
            state.selectedMaintenance = null;
            state.selectedMaintenanceError = null;
        },
        resetCreateStatus: (state) => {
            state.createMaintenanceLoading = false;
            state.createMaintenanceError = null;
        },
        resetUpdateStatus: (state) => {
            state.updateMaintenanceLoading = false;
            state.updateMaintenanceError = null;
        },
        resetDeleteStatus: (state) => {
            state.deleteMaintenanceLoading = false;
            state.deleteMaintenanceError = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // gestione getMaintenancesList
            .addCase(getMaintenancesList.pending, (state) => {
                state.maintenancesLoading = true;
                state.maintenancesError = null;
            })
            .addCase(getMaintenancesList.fulfilled, (state, action) => {
                state.maintenancesLoading = false;
                // L'API restituisce già un oggetto formattato correttamente per DataTables
                state.maintenancesList = action.payload.data;
                state.maintenancesTotalRecords = action.payload.totalRecords;
                console.log('Maintenance payload received:', action.payload);
            })
            .addCase(getMaintenancesList.rejected, (state, action) => {
                state.maintenancesLoading = false;
                state.maintenancesError = action.payload;
            })
            
            // gestione getMaintenanceDetail
            .addCase(getMaintenanceDetail.pending, (state) => {
                state.selectedMaintenanceLoading = true;
                state.selectedMaintenanceError = null;
            })
            .addCase(getMaintenanceDetail.fulfilled, (state, action) => {
                state.selectedMaintenanceLoading = false;
                state.selectedMaintenance = action.payload;
            })
            .addCase(getMaintenanceDetail.rejected, (state, action) => {
                state.selectedMaintenanceLoading = false;
                state.selectedMaintenanceError = action.payload;
            })
            
            // gestione createMaintenance
            .addCase(createMaintenance.pending, (state) => {
                state.createMaintenanceLoading = true;
                state.createMaintenanceError = null;
            })
            .addCase(createMaintenance.fulfilled, (state, action) => {
                state.createMaintenanceLoading = false;
                if (state.maintenancesList) {
                    state.maintenancesList.push(action.payload);
                }
            })
            .addCase(createMaintenance.rejected, (state, action) => {
                state.createMaintenanceLoading = false;
                state.createMaintenanceError = action.payload;
            })
            
            // gestione updateMaintenance
            .addCase(updateMaintenance.pending, (state) => {
                state.updateMaintenanceLoading = true;
                state.updateMaintenanceError = null;
            })
            .addCase(updateMaintenance.fulfilled, (state, action) => {
                state.updateMaintenanceLoading = false;
                const updatedIndex = state.maintenancesList.findIndex(m => m._id === action.payload._id);
                if (updatedIndex !== -1) {
                    state.maintenancesList[updatedIndex] = action.payload;
                }
                state.selectedMaintenance = action.payload;
            })
            .addCase(updateMaintenance.rejected, (state, action) => {
                state.updateMaintenanceLoading = false;
                state.updateMaintenanceError = action.payload;
            })
            
            // gestione deleteMaintenance
            .addCase(deleteMaintenance.pending, (state) => {
                state.deleteMaintenanceLoading = true;
                state.deleteMaintenanceError = null;
            })
            .addCase(deleteMaintenance.fulfilled, (state, action) => {
                state.deleteMaintenanceLoading = false;
                state.maintenancesList = state.maintenancesList.filter(m => m._id !== action.payload);
            })
            .addCase(deleteMaintenance.rejected, (state, action) => {
                state.deleteMaintenanceLoading = false;
                state.deleteMaintenanceError = action.payload;
            });
    }
});

// Export actions
export const { 
    resetSelectedMaintenance, 
    resetCreateStatus, 
    resetUpdateStatus, 
    resetDeleteStatus 
} = maintenancesSlice.actions;

// Export selectors
export const selectMaintenancesList = (state) => state.maintenances.maintenancesList;
export const selectMaintenancesTotalRecords = (state) => state.maintenances.maintenancesTotalRecords;
export const selectMaintenancesLoading = (state) => state.maintenances.maintenancesLoading;
export const selectMaintenancesError = (state) => state.maintenances.maintenancesError;
export const selectSelectedMaintenance = (state) => state.maintenances.selectedMaintenance;
export const selectSelectedMaintenanceLoading = (state) => state.maintenances.selectedMaintenanceLoading;
export const selectSelectedMaintenanceError = (state) => state.maintenances.selectedMaintenanceError;
export const selectCreateMaintenanceLoading = (state) => state.maintenances.createMaintenanceLoading;
export const selectCreateMaintenanceError = (state) => state.maintenances.createMaintenanceError;
export const selectUpdateMaintenanceLoading = (state) => state.maintenances.updateMaintenanceLoading;
export const selectUpdateMaintenanceError = (state) => state.maintenances.updateMaintenanceError;
export const selectDeleteMaintenanceLoading = (state) => state.maintenances.deleteMaintenanceLoading;
export const selectDeleteMaintenanceError = (state) => state.maintenances.deleteMaintenanceError;

// Export reducer
export default maintenancesSlice.reducer;
